import { ArrowLeft, TrendingUp, TrendingDown, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { LaneData } from "@/hooks/useLaneData";

interface CarrierLaneVolumePaneProps {
  onBack: () => void;
  lanes: LaneData[];
  globalAverage: number;
  equipment: string;
  isLoading: boolean;
}

/**
 * Carrier-side AI Lane Volume surface.
 * Reuses the dashboard's already-loaded useLaneData() result — no new query.
 * Surfaces volume + RPM-vs-market deltas for the currently selected equipment.
 */
export function CarrierLaneVolumePane({ onBack, lanes, globalAverage, equipment, isLoading }: CarrierLaneVolumePaneProps) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      <div className="flex items-center gap-2 border-b px-3 py-2.5 bg-muted/20">
        <button onClick={onBack} className="p-1 rounded hover:bg-muted transition-colors">
          <ArrowLeft className="h-4 w-4 text-muted-foreground" />
        </button>
        <BarChart3 className="h-4 w-4 text-accent" />
        <h4 className="text-caption font-bold text-foreground">AI Lane Volume</h4>
        <span className="ml-auto text-[10px] font-medium text-muted-foreground capitalize">
          {equipment} · {lanes.length} lanes
        </span>
      </div>

      <div className="px-3 py-2 border-b bg-muted/10">
        <p className="text-[11px] text-muted-foreground">
          Volume + rate moves on the lanes feeding your <span className="font-semibold capitalize text-foreground">{equipment}</span> heat map.
          Switch equipment in the RPM Market Index panel to refilter.
        </p>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-2">
            <BarChart3 className="h-8 w-8 text-muted-foreground/40 animate-pulse" />
            <p className="text-sm text-muted-foreground">Loading lane volume…</p>
          </div>
        ) : lanes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-2">
            <BarChart3 className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Track lanes you care about.</p>
            <p className="text-xs text-muted-foreground/70">
              Save searches and we'll watch volume + rate moves for you.
            </p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate("/search")}>
              Open search
            </Button>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-[1fr_80px_90px_90px_80px] items-center px-3 py-1 border-b border-border/50">
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Lane</span>
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Volume</span>
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Avg RPM</span>
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">vs Market</span>
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Action</span>
            </div>
            {lanes.map((lane, i) => {
              const delta = globalAverage > 0 ? ((lane.rate - globalAverage) / globalAverage) * 100 : 0;
              const positive = delta >= 0;
              return (
                <div
                  key={`${lane.shortName}-${i}`}
                  className={`grid grid-cols-[1fr_80px_90px_90px_80px] items-center px-3 py-2 border-b border-border/30 hover:bg-muted/30 transition-colors ${i % 2 === 1 ? "bg-muted/20" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                      <span className="truncate">{lane.shortName}</span>
                      {lane.isTop && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-primary/15 text-primary">TOP</span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                      {lane.originCity}, {lane.originState} → {lane.destCity}, {lane.destState}
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold text-foreground tabular-nums text-right">
                    {lane.volume.toLocaleString()}
                  </span>
                  <span className="text-[11px] font-semibold text-primary tabular-nums text-right">
                    {lane.rate > 0 ? `$${lane.rate.toFixed(2)}` : "—"}
                  </span>
                  <span className={`text-[10px] font-semibold tabular-nums text-right inline-flex items-center justify-end gap-0.5 ${positive ? "text-success" : "text-destructive"}`}>
                    {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {positive ? "+" : ""}{delta.toFixed(1)}%
                  </span>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] px-2.5 flex-shrink-0"
                      onClick={() => navigate(`/search?origin=${encodeURIComponent(lane.originCity + ", " + lane.originState)}&destination=${encodeURIComponent(lane.destCity + ", " + lane.destState)}&equipment=${encodeURIComponent(equipment)}`)}
                    >
                      Search
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
